<!-- ═══ Add Element ═══ -->
<div class="mb-3 flex items-end gap-1.5">
  <dojo-single-select
    [formCtrl]="newEntryTypeCtrl"
    [options]="allAddOptions"
    optionLabel="label"
    optionValue="value"
    [size]="'s'"
    placeholder="Add element..."
    class="flex-1"
  />
  <dojo-text-input
    [formCtrl]="newEntryIdCtrl"
    [size]="'s'"
    [placeholder]="defaultNewId() || 'Insert id'"
    class="w-20"
    (keydown.enter)="onAddEntry()"
  />
  <dojo-button [size]="'s'" customColor="sky" (click)="onAddEntry()">Add</dojo-button>
</div>

<!-- ═══ Flat List ═══ -->
<mat-accordion class="custom-mat-accordion" [multi]="true">
  <!-- Coordinate System (always first) -->
  <mat-expansion-panel>
    <mat-expansion-panel-header>
      <mat-panel-title class="flex items-center gap-2 text-sm">
        <span class="font-medium">Coordinate System</span>
        <span class="rounded bg-neutral-100 px-1.5 py-px text-[10px] text-neutral-400">coord</span>
      </mat-panel-title>
      <mat-panel-description class="!flex-grow-0">
        <div class="w-5"></div>
      </mat-panel-description>
    </mat-expansion-panel-header>

    <div class="flex flex-col gap-2 p-4">
      <!-- Bounding Box -->
      <div class="text-[10px] font-semibold uppercase tracking-wider text-neutral-400">Bounding Box</div>
      <div class="grid grid-cols-2 gap-1.5">
        <dojo-number-input label="x Start" [formCtrl]="xStartCtrl" [step]="1" [size]="'s'" />
        <dojo-number-input label="x End" [formCtrl]="xEndCtrl" [step]="1" [size]="'s'" />
        <dojo-number-input label="y Start" [formCtrl]="yStartCtrl" [step]="1" [size]="'s'" />
        <dojo-number-input label="y End" [formCtrl]="yEndCtrl" [step]="1" [size]="'s'" />
      </div>

      <!-- Fixed Axis (aspect ratio) -->
      <dojo-single-select
        label="Fixed Axis"
        [formCtrl]="fixedAxisCtrl"
        [options]="fixedAxisOptions"
        optionLabel="label"
        optionValue="value"
        [size]="'s'"
        (selectionChange)="onFixedAxisChange($event)"
      />

      <!-- Grid -->
      <mat-slide-toggle [checked]="gridVisible()" (change)="onGridVisibleChange($event.checked)">
        Grid
      </mat-slide-toggle>
      @if (gridVisible()) {
        <dojo-single-select
          label="Type"
          [formCtrl]="gridTypeCtrl"
          [options]="gridTypeOptions"
          optionLabel="label"
          optionValue="value"
          [size]="'s'"
          (selectionChange)="onGridTypeChange($event)"
        />
      }

      <!-- Axes -->
      <dojo-single-select
        label="Axes"
        [formCtrl]="axesModeCtrl"
        [options]="axesModeOptions"
        optionLabel="label"
        optionValue="value"
        [size]="'s'"
        (selectionChange)="onAxesModeChange($event)"
      />

      @if (axesMode() === 'custom') {
        <div class="flex flex-col gap-2 border-l-2 border-neutral-100 pl-3">
          @for (axis of axesArray(); track axis.id; let idx = $index) {
            <div class="flex flex-col gap-1.5 rounded border border-neutral-100 p-2">
              <div class="flex items-center justify-between">
                <span class="text-xs font-medium">{{ axis.id }}</span>
                <dojo-icon-button icon="trash" [size]="'xs'" (click)="onDeleteAxis(idx)" />
              </div>
              <div class="grid grid-cols-2 gap-1.5">
                <dojo-single-select
                  label="Orientation"
                  [formCtrl]="getAxisCtrl(idx, 'orientation')"
                  [options]="axisOrientationOptions"
                  optionLabel="label"
                  optionValue="value"
                  [size]="'s'"
                  (selectionChange)="onAxisFieldChange(idx, 'orientation', $event)"
                />
                <dojo-number-input
                  label="Position"
                  [formCtrl]="getAxisNumberCtrl(idx, 'position')"
                  [step]="1"
                  [size]="'s'"
                />
              </div>
              <mat-slide-toggle
                [checked]="axis.isPrimary === true"
                (change)="onAxisFieldChange(idx, 'isPrimary', $event.checked)"
              >
                Primary
              </mat-slide-toggle>

              <!-- Axis Ticks -->
              <div class="mt-1 text-[10px] font-semibold uppercase tracking-wider text-neutral-400">Ticks</div>
              <div class="grid grid-cols-2 gap-1.5">
                <dojo-number-input
                  label="Distance"
                  [formCtrl]="getAxisNumberCtrl(idx, 'ticks.ticksDistance')"
                  [step]="0.5"
                  [min]="0.1"
                  [size]="'s'"
                />
                <dojo-single-select
                  label="Format"
                  [formCtrl]="getAxisCtrl(idx, 'ticks.labelFormat')"
                  [options]="labelFormatOptions"
                  optionLabel="label"
                  optionValue="value"
                  [size]="'s'"
                  (selectionChange)="onAxisTicksFieldChange(idx, 'labelFormat', $event)"
                />
              </div>

              <!-- Axis Label -->
              <div class="mt-1 text-[10px] font-semibold uppercase tracking-wider text-neutral-400">Label</div>
              <dojo-text-input label="Text" [formCtrl]="getAxisTextCtrl(idx, 'axisLabel.text')" [size]="'s'" />
              <dojo-number-input
                label="Font Size"
                [formCtrl]="getAxisNumberCtrl(idx, 'axisLabel.fontSize')"
                [step]="1"
                [min]="6"
                [max]="24"
                [size]="'s'"
              />
            </div>
          }
          <button
            class="flex items-center gap-1 rounded px-1.5 py-1 text-[11px] text-neutral-400 hover:bg-neutral-50 hover:text-neutral-600"
            (click)="onAddAxis()"
          >
            <dojo-icon icon="plus" [size]="10" />
            Add Axis
          </button>
        </div>
      }

      <!-- Appearance -->
      <div
        class="mt-1 border-t border-neutral-100 pt-2 text-[10px] font-semibold uppercase tracking-wider text-neutral-400"
      >
        Appearance
      </div>
      <div class="grid grid-cols-2 gap-1.5">
        <dojo-single-select
          label="Background"
          [formCtrl]="bgColorCtrl"
          [options]="bgColorOptions"
          optionLabel="label"
          optionValue="value"
          [size]="'s'"
          placeholder="Default"
          (selectionChange)="onBgColorChange($event)"
        />
        <dojo-number-input
          label="Precision"
          [formCtrl]="defaultPrecisionCtrl"
          [step]="1"
          [min]="0"
          [max]="10"
          [size]="'s'"
        />
      </div>
      <mat-slide-toggle [checked]="borderVisible()" (change)="onBorderVisibleChange($event.checked)">
        Border
      </mat-slide-toggle>
      @if (borderVisible()) {
        <dojo-single-select
          label="Border Color"
          [formCtrl]="borderColorCtrl"
          [options]="borderColorOptions"
          optionLabel="label"
          optionValue="value"
          [size]="'s'"
          placeholder="Default (neutral-200)"
          (selectionChange)="onBorderColorChange($event)"
        />
      }

      <!-- Ticks -->
      <div class="mt-1 flex items-center justify-between border-t border-neutral-100 pt-2">
        <span class="text-[10px] font-semibold uppercase tracking-wider text-neutral-400">Ticks</span>
        <mat-slide-toggle [checked]="ticksVisible()" (change)="onTicksVisibleChange($event.checked)" />
      </div>
      @if (ticksVisible()) {
        <div class="flex flex-col gap-1.5 border-l-2 border-neutral-100 pl-3">
          <div class="grid grid-cols-2 gap-1.5">
            <dojo-number-input label="Distance" [formCtrl]="ticksDistanceCtrl" [step]="0.5" [min]="0.1" [size]="'s'" />
            <dojo-number-input
              label="Minor Ticks"
              [formCtrl]="minorTicksCtrl"
              [step]="1"
              [min]="0"
              [max]="10"
              [size]="'s'"
            />
          </div>
          <div class="flex items-center gap-3">
            <mat-slide-toggle [checked]="drawLabelsCtrl.value" (change)="onDrawLabelsChange($event.checked)">
              Draw Labels
            </mat-slide-toggle>
            <mat-slide-toggle [checked]="drawZeroCtrl.value" (change)="onDrawZeroChange($event.checked)">
              Draw Zero
            </mat-slide-toggle>
          </div>
          <dojo-single-select
            label="Label Format"
            [formCtrl]="labelFormatCtrl"
            [options]="labelFormatOptions"
            optionLabel="label"
            optionValue="value"
            [size]="'s'"
            (selectionChange)="onLabelFormatChange($event)"
          />
        </div>
      }
    </div>
  </mat-expansion-panel>

  <!-- ═══ All entries: functions → features → primitives → controls ═══ -->
  @for (entry of flatEntries(); track entry.trackId) {
    <mat-expansion-panel>
      <mat-expansion-panel-header>
        <mat-panel-title class="flex items-center gap-2 text-sm">
          <span class="font-medium">{{ entry.displayName }}</span>
          <span class="rounded bg-neutral-100 px-1.5 py-px text-[10px] text-neutral-400">{{ entry.badge }}</span>
        </mat-panel-title>
        <mat-panel-description class="!mr-0 !flex-grow-0">
          <dojo-icon
            icon="trash"
            [size]="12"
            class="cursor-pointer text-sky-400 hover:text-sky-600"
            (click)="onDeleteEntry(entry); $event.stopPropagation()"
          />
        </mat-panel-description>
      </mat-expansion-panel-header>

      <div class="p-4">
        <!-- Element / Function / Primitive editor -->
        @if (entry.def) {
          <dojo-element-editor
            [elementId]="entry.id"
            [elementDef]="entry.def"
            [elementDisplay]="entry.display"
            [functionElementIds]="functionElementIds()"
            [allElementIds]="allElementIds()"
            (paramsChange)="onElementParamsChange(entry.id, entry, $event)"
            (displayChange)="onElementDisplayChange(entry.id, $event)"
          />
        }

        <!-- Control editor (inline) -->
        @if (entry.control) {
          <div class="flex flex-col gap-1.5">
            <div class="grid grid-cols-2 gap-1.5">
              <dojo-text-input label="Label" [formCtrl]="getCtrlTextCtrl(entry.control.id, 'label')" [size]="'s'" />
              <dojo-single-select
                label="Position"
                [formCtrl]="getCtrlSelectCtrl(entry.control.id, 'position')"
                [options]="controlPositionOptions"
                optionLabel="label"
                optionValue="value"
                [size]="'s'"
                placeholder="Default"
                (selectionChange)="onControlFieldChange(entry.controlIndex!, 'position', $event)"
              />
            </div>

            <!-- Slider-specific -->
            @if (entry.control.type === 'slider') {
              <dojo-text-input
                label="Param"
                [formCtrl]="getCtrlTextCtrl(entry.control.id, 'param')"
                [size]="'s'"
                placeholder="f.a"
              />
              <div class="grid grid-cols-3 gap-1.5">
                <dojo-number-input
                  label="Min"
                  [formCtrl]="getCtrlNumberCtrl(entry.control.id, 'rangeMin')"
                  [step]="1"
                  [size]="'s'"
                />
                <dojo-number-input
                  label="Max"
                  [formCtrl]="getCtrlNumberCtrl(entry.control.id, 'rangeMax')"
                  [step]="1"
                  [size]="'s'"
                />
                <dojo-number-input
                  label="Step"
                  [formCtrl]="getCtrlNumberCtrl(entry.control.id, 'rangeStep')"
                  [step]="0.1"
                  [min]="0.01"
                  [size]="'s'"
                />
              </div>
              <div class="grid grid-cols-2 gap-1.5">
                <dojo-number-input
                  label="Default"
                  [formCtrl]="getCtrlNumberCtrl(entry.control.id, 'defaultValue')"
                  [step]="0.1"
                  [size]="'s'"
                />
                <dojo-single-select
                  label="Color"
                  [formCtrl]="getCtrlSelectCtrl(entry.control.id, 'color')"
                  [options]="controlColorOptions"
                  optionLabel="label"
                  optionValue="value"
                  [size]="'s'"
                  placeholder="Default"
                  (selectionChange)="onControlFieldChange(entry.controlIndex!, 'color', $event)"
                />
              </div>
              <div class="grid grid-cols-2 gap-1.5">
                <dojo-single-select
                  label="Variant"
                  [formCtrl]="getCtrlSelectCtrl(entry.control.id, 'variant')"
                  [options]="controlVariantOptions"
                  optionLabel="label"
                  optionValue="value"
                  [size]="'s'"
                  placeholder="Default"
                  (selectionChange)="onControlFieldChange(entry.controlIndex!, 'variant', $event)"
                />
                <mat-slide-toggle
                  class="self-center"
                  [checked]="isSliderEditable(entry.control)"
                  (change)="onControlFieldChange(entry.controlIndex!, 'editable', $event.checked)"
                >
                  Editable
                </mat-slide-toggle>
              </div>
            }

            <!-- Button-specific -->
            @if (entry.control.type === 'button') {
              <div class="grid grid-cols-2 gap-1.5">
                <dojo-text-input
                  label="Icon"
                  [formCtrl]="getCtrlTextCtrl(entry.control.id, 'icon')"
                  [size]="'s'"
                  placeholder="magnifying-glass-plus"
                />
                <mat-slide-toggle
                  class="self-center"
                  [checked]="isButtonShowLabel(entry.control)"
                  (change)="onControlFieldChange(entry.controlIndex!, 'showLabel', $event.checked)"
                >
                  Show Label
                </mat-slide-toggle>
              </div>
            }
          </div>
        }
      </div>
    </mat-expansion-panel>
  }
</mat-accordion>
